import { TrendingUp, TrendingDown, ArrowUpRight } from "lucide-react";
import { COMPARABLES, PRESS } from "@/data/content";
import { SectionLabel, CountUp, Reveal } from "@/components/site/primitives";
import { trackClick } from "@/lib/utils";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const MarketValidation = () => {
  return (
    <section
      id="validation"
      data-testid="validation-section"
      className="mx-auto max-w-[1400px] px-5 md:px-10 py-28 md:py-44"
    >
      <SectionLabel index="[ 04 ]" testId="validation-label">
        Market Validation
      </SectionLabel>

      <div className="grid md:grid-cols-2 gap-16 md:gap-24">
        {/* left: competitor raises */}
        <div>
          <Reveal>
            <h3 className="font-grotesk font-bold uppercase tracking-tight text-3xl md:text-5xl leading-[1.05] text-epic-text mb-6">
              The category
              <br />
              is <span className="text-epic-accent">proven.</span>
            </h3>
            <p className="font-mono text-sm text-epic-sub leading-relaxed max-w-md mb-12">
              The top three providers — Wealth.com, Vanilla, and Trust &amp; Will — have raised more
              than $274 million combined.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <p
              data-testid="combined-raised"
              className="font-grotesk font-bold tracking-tight text-6xl md:text-8xl text-epic-text mb-2"
            >
              <CountUp prefix="$" value={COMPARABLES.combined} suffix="M+" />
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-epic-sub mb-10">
              Combined capital raised by category leaders
            </p>

            <div data-testid="raises-table">
              {COMPARABLES.raises.map((r) => (
                <div
                  key={r.name}
                  data-testid={`raise-${slug(r.name)}`}
                  className="flex items-baseline justify-between gap-4 border-t border-epic-line py-4"
                >
                  <span className="font-grotesk font-semibold uppercase tracking-wide text-epic-text">
                    {r.name}
                  </span>
                  <span className="font-mono text-sm text-epic-sub">{r.amount}</span>
                </div>
              ))}
              <p className="border-t border-epic-line pt-4 font-mono text-[11px] text-epic-sub/70">
                Based on publicly reported figures.
              </p>
            </div>
          </Reveal>
        </div>

        {/* right: category activity board */}
        <div>
          <Reveal delay={0.15}>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-epic-sub mb-6">
              Category activity — LinkedIn competitor analytics
            </p>
            <div data-testid="activity-board" className="border border-epic-line">
              {COMPARABLES.activity.map((row) => (
                <div
                  key={row.name}
                  data-testid={`activity-${slug(row.name)}`}
                  className={`flex items-center gap-4 px-5 py-4 border-b border-epic-line last:border-b-0 ${
                    row.highlight ? "bg-epic-accent/5 border-l-2 border-l-epic-accent" : ""
                  }`}
                >
                  <span className="font-mono text-xs text-epic-sub w-5 shrink-0">{row.rank}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-grotesk font-semibold text-epic-text flex items-center gap-2 flex-wrap">
                      {row.name}
                      {row.highlight && (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-epic-accent border border-epic-accent px-1.5 py-0.5">
                          You
                        </span>
                      )}
                    </p>
                    <p className="font-mono text-xs text-epic-sub">
                      {row.followers} followers · {row.posts} posts
                    </p>
                  </div>
                  <span
                    className={`flex items-center gap-1.5 font-mono text-sm shrink-0 ${
                      row.delta >= 0 ? "text-epic-accent" : "text-epic-sub/70"
                    }`}
                  >
                    {row.delta >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {row.delta >= 0 ? "+" : "-"}
                    {Math.abs(row.delta)}%
                  </span>
                </div>
              ))}
            </div>
            <p className="font-mono text-sm text-epic-sub leading-relaxed mt-8">
              ePIC is the only top-four player still accelerating —{" "}
              <span className="text-epic-text">+146.7% activity growth</span> while the funded
              leaders pull back.
            </p>
          </Reveal>
        </div>
      </div>

      {/* press pull-quote */}
      <Reveal delay={0.1}>
        <figure data-testid="press-callout" className="border-l-2 border-epic-accent pl-6 md:pl-10 mt-16 md:mt-20 max-w-4xl">
          <blockquote className="font-editorial italic text-2xl md:text-4xl leading-tight text-epic-text">
            &ldquo;{PRESS.quote}&rdquo;
          </blockquote>
          <figcaption className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub mt-5">
            {PRESS.source} · {PRESS.date}
          </figcaption>
          <a
            href={PRESS.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="press-link"
            onClick={() => trackClick("press-mutual-of-omaha")}
            className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-epic-accent transition-colors duration-200 hover:text-epic-text"
          >
            Read the announcement
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </figure>
      </Reveal>

      {/* bridge into The Raise */}
      <Reveal delay={0.2}>
        <p
          data-testid="validation-bridge"
          className="font-grotesk font-bold uppercase tracking-tight text-2xl md:text-4xl leading-tight text-epic-text mt-20 md:mt-28 max-w-4xl"
        >
          The incumbents raised $274M+ to get here.{" "}
          <span className="text-epic-accent">Your entry point is a $2.5M raise.</span>
        </p>
      </Reveal>
    </section>
  );
};
